import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { CustomerProvider } from "@/context/CustomerContext";
import { AuthCallback } from "@/components/AuthCallback";
import { Navbar } from "@/components/Navbar";
import { PromoBanner } from "@/components/PromoBanner";

const Home = lazy(() => import("@/pages/Home"));
const AlterEgoChat = lazy(() => import("@/components/AlterEgoChat"));
const EbookStudio = lazy(() => import("@/pages/EbookStudio"));
const EbookReader = lazy(() => import("@/pages/EbookReader"));
const MyPurchases = lazy(() => import("@/pages/MyPurchases"));
const Promote = lazy(() => import("@/pages/Promote"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const Blog = lazy(() => import("@/pages/Blog"));
const Articles = lazy(() => import("@/pages/Articles"));
const ArticleReader = lazy(() => import("@/pages/ArticleReader"));
const TopicPage = lazy(() => import("@/pages/TopicPage"));
const BookLanding = lazy(() => import("@/pages/BookLanding"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const SalesDashboard = lazy(() => import("@/pages/SalesDashboard"));

const PageLoader = () => (
  <div
    data-testid="page-loader"
    className="min-h-[60vh] flex items-center justify-center"
  >
    <div className="h-6 w-6 rounded-full border-2 border-white/15 border-t-[#F59E0B] animate-spin" />
  </div>
);

const Layout = () => (
  <div className="min-h-screen bg-[#030303] text-[#F5F5F5] relative">
    <Navbar />
    <PromoBanner />
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  </div>
);

// GetAutoSEO publishes article links as /blog/{slug}; serve them from /articles/{slug}
const BlogSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/articles/${slug}`} replace />;
};

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  // Google OAuth returns to {origin}/studio/purchases#session_id=... — process it first
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/chat" element={<AlterEgoChat />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/studio" element={<EbookStudio />} />
        <Route path="/studio/book/:id" element={<EbookReader />} />
        <Route path="/book/:slug" element={<BookLanding />} />
        <Route path="/author/:slug" element={<AuthorPage />} />
        <Route path="/studio/purchases" element={<MyPurchases />} />
        <Route path="/studio/sales" element={<SalesDashboard />} />
        <Route path="/studio/restore" element={<Navigate to="/studio/purchases" replace />} />
        <Route path="/promote" element={<Promote />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogSlugRedirect />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<ArticleReader />} />
        <Route path="/topics/:slug" element={<TopicPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <CustomerProvider>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <AppRouter />
              </Suspense>
            </BrowserRouter>
          </CustomerProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
